import type { ReactNode } from 'react'

import { CompanyOverview } from './CompanyOverview'

export type DocLabel = 'verified' | 'outdated'

export type DocIcon = {
  shape?: string | null
  color?: string | null
}

export type DocNode = {
  id: string
  title: string
  icon?: DocIcon
  content?: string
  // Bespoke React node rendered in place of `content` (e.g. the Company
  // overview landing page). Scenarios stick to markdown via `content`.
  customRender?: ReactNode
  label?: DocLabel
  isSelfMaintained?: boolean
  children?: DocNode[]
}

const doc = (
  id: string,
  title: string,
  icon: DocIcon,
  content?: string
): DocNode => ({
  id,
  title,
  icon,
  content: content ?? `# ${title}`,
  label: 'verified',
})

const folder = (
  id: string,
  title: string,
  icon: DocIcon,
  children: DocNode[]
): DocNode => ({ id, title, icon, children })

const DATA_RETENTION_POLICY = `# Data Retention Policy

Last reviewed: March 2022

## Purpose
This policy defines how The Nice Co. retains and disposes of data to meet
legal, regulatory, and operational requirements.

## Retention Periods

- **Customer records:** 90 days (AWS S3, eu-west)
- **Support tickets:** 1 year (Intercom)
- **Employee records:** 5 years (Google Drive)
- **Financial records:** 7 years (NetSuite)
- **Application logs:** 30 days (Datadog)

## Deletion
Data exceeding its retention period is deleted on the 1st of each
month via automated pipeline. Exceptions require VP approval.

## Review Cycle
This policy is reviewed annually by the Legal and Security teams.

## Owner
Legal: legal@acme.com
`

const INTEGRATIONS = `# Integrations

Last reviewed: January 2025

Slite connects to the tools your team already uses.

## CSV Import
Import existing documentation in bulk using a CSV file, including section
metadata, owners, and review dates from older help center exports.
Supports up to 500 rows per import.

## Slack
Receive doc update notifications in Slack and create docs from any message,
so support and product teams can turn recurring answers into trusted docs.

## GitHub
Link pull requests and issues directly inside docs.

## Zapier
Connect Slite to 5,000+ apps via Zapier.
`

const TROUBLESHOOTING = `# Troubleshooting

Common issues and how to fix them.

## Can't log in
Make sure you're using the email address tied to your
Slite workspace. Reset your password from the login page
if needed.

## Docs not loading
Try a hard refresh (Cmd+Shift+R) or clear your browser
cache. If the issue persists, try a different browser.

## Search not returning results
Re-index your workspace from Settings → Advanced →
Reindex. Results update within a few minutes.

## Notifications not arriving
Check that notifications are enabled in Settings →
Notifications and that Slite isn't blocked by your
browser or OS.
`

export const INITIAL_TREE: DocNode[] = [
  {
    ...doc('company', 'Company', { shape: 'house', color: 'orange' }),
    customRender: <CompanyOverview />,
  },
  folder(
    'help-center',
    'Help Center',
    { shape: 'ring_buoy', color: 'yellow' },
    [
      folder(
        'getting-started',
        'Getting Started',
        { shape: 'file_folder', color: 'yellow' },
        [
          doc('quick-start-guide', 'Quick Start Guide', {
            shape: 'rocket',
            color: 'yellow',
          }),
        ]
      ),
      folder('features', 'Features', { shape: 'sparkles', color: 'purple' }, [
        doc('permissions-access', 'Access', { shape: 'key', color: 'grey' }),
        doc(
          'integrations',
          'Integrations',
          { shape: 'electric_plug', color: 'blue' },
          INTEGRATIONS
        ),
      ]),
      {
        ...doc(
          'troubleshooting',
          'Troubleshooting',
          { shape: 'hammer_and_wrench', color: 'grey' },
          TROUBLESHOOTING
        ),
        isSelfMaintained: true,
      },
    ]
  ),
  folder('compliance', 'Compliance', { shape: 'scales', color: 'purple' }, [
    doc(
      'data-retention-policy',
      'Data Retention Policy',
      { shape: 'scroll', color: 'blue' },
      DATA_RETENTION_POLICY
    ),
    doc('security-overview', 'Security Overview', {
      shape: 'eye',
      color: 'green',
    }),
    doc('soc-2-controls', 'SOC 2 Controls', {
      shape: 'white_check_mark',
      color: 'green',
    }),
  ]),
  folder('engineering', 'Engineering', { shape: 'hammer', color: 'blue' }, [
    doc('release-checklist', 'Release Checklist', {
      shape: 'clipboard',
      color: 'grey',
    }),
    doc('deployment-guide', 'Deployment Guide', {
      shape: 'rocket',
      color: 'blue',
    }),
    folder('services', 'Services', { shape: 'gear', color: 'grey' }, [
      doc('auth-service', 'Auth Service', { shape: 'key', color: 'orange' }),
      doc('api-gateway', 'API Gateway', { shape: 'gear', color: 'grey' }),
    ]),
  ]),
  folder('product', 'Product', { shape: 'bulb', color: 'orange' }, [
    doc('changelog', 'Changelog', { shape: 'mega', color: 'pink' }),
    doc('feature-flags', 'Feature Flags', {
      shape: 'triangular_flag_on_post',
      color: 'orange',
    }),
  ]),
  folder(
    'people-ops',
    'People & Ops',
    { shape: 'busts_in_silhouette', color: 'pink' },
    [
      doc('onboarding-guide', 'Onboarding Guide', {
        shape: 'wave',
        color: 'yellow',
      }),
      doc('team-handbook', 'Team Handbook', {
        shape: 'book',
        color: 'turquoise',
      }),
    ]
  ),
]
